import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { RootState } from '../../store';
import FormModal from '../../components/modals/form.modal';
import AuctionForm from '../../components/forms/auction.form';
import { showSnackbar } from '../../store/slices/common.slice';
import MyAuctionGridWidget from '../../components/widgets/my-account/my-auction-grid.widget';
import CustomSwal from '../../components/elements/custom-swal.element';
import ProgressModal from '../../components/modals/progress.modal';
import { getApiErrorMessage, userIsSeller } from '../../utilities/helper';
import { deleteAuction } from '../../apis';
import HorizontalTabs from '../../components/elements/horizontal-tabs.element';
import EnrolledAuctionGridWidget from '../../components/widgets/my-account/enrolled-auction-grid.widget';
import NonEnrolledAuctionGridWidget from '../../components/widgets/my-account/non-enrolled-auction-grid.widget';
import useAuctionBroadcastWsHook from '../../hooks/auction-broadcast-ws.hook';
import useNavigateHook from '../../hooks/navigate.hook';

const MyAccountAuctionsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { doNavigate } = useNavigateHook();

  const { user, token } = useSelector((state: RootState) => state.auth);
  const [showAuctionFormModal, setShowAuctionFormModal] = React.useState(false);
  const [showDeletingModal, setShowDeletingModal] = React.useState(false);
  const [refreshAuctionList, setRefreshAuctionList] = React.useState<number>(1);
  const [auctionDetail, setAuctionDetail] = React.useState<any | null>(null);
  const [selectedTab, setSelectedTab] = React.useState<number>(0);

  const onAuctionCreate = () => {
    setRefreshAuctionList(refreshAuctionList + 1);
    CustomSwal.fire({
      icon: 'success',
      title: 'Done',
      text: `Auction created successfully`,
    });
    dispatch(showSnackbar({ message: 'Auction created successfully', severity: 'success' }));
    setShowAuctionFormModal(false);
  };

  const onEdit = async (auction: any) => {
    setAuctionDetail(auction);
    setShowAuctionFormModal(true);
  };

  const onDelete = (auction: any) => {
    CustomSwal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setShowDeletingModal(true);
        try {
          const response = await deleteAuction({ id: auction.id });
          if (response.status) {
            setRefreshAuctionList(refreshAuctionList + 1);
            CustomSwal.fire({
              title: 'Success',
              text: 'Auction delete successfully',
              icon: 'success',
            });
          }
        } catch (err: any) {
          CustomSwal.fire({
            title: 'Error',
            text: getApiErrorMessage(err),
            icon: 'error',
          });
        }
        setShowDeletingModal(false);
      }
    });
  };

  const onView = async (auction: any) => {
    doNavigate(`/my-account/auction-detail/${auction.id}`);
  };

  useAuctionBroadcastWsHook(token, () => {
    setRefreshAuctionList(refreshAuctionList + 1);
  });

  let auctionTabs = [
    { label: 'Live Auctions', value: 0 },
    { label: 'Coming Auctions', value: 1 },
    { label: 'Past Auctions', value: 2 },
  ];
  if (!userIsSeller(user)) {
    auctionTabs = [
      { label: 'Enrolled Auctions', value: 0 },
      { label: 'Other Auctions', value: 1 },
    ];
  }

  return (
    <>
      <Stack
        direction={{ md: 'row', xs: 'column' }}
        alignItems={{ md: 'center', xs: 'start' }}
        justifyContent="space-between"
        gap={2}
      >
        <Box>
          <Typography variant="h1" gutterBottom sx={{ fontWeight: '600' }}>
            My Auctions
          </Typography>
          <Typography variant="body1" gutterBottom color="textSecondary">
            {userIsSeller(user)
              ? `Below is the list of all auctions that you have created.`
              : `Below is the list of all auctions which are currently live and you have placed bid.`}
          </Typography>
        </Box>
        {userIsSeller(user) && (
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              variant="contained"
              color="info"
              disabled // forcefully disabled as per client need
              onClick={() => {
                setShowAuctionFormModal(true);
              }}
            >
              Create Auction
            </Button>
          </Box>
        )}
      </Stack>
      <Box>
        <Box sx={{ my: 4, px: 4, mx: 'auto', maxWidth: '800px', textAlign: 'center' }}>
          <HorizontalTabs
            items={auctionTabs}
            selectedTab={selectedTab}
            onChange={(newValue: string | number) => {
              setSelectedTab(newValue as number);
            }}
          />
        </Box>
        <Box>
          {selectedTab === 0 &&
            refreshAuctionList &&
            (userIsSeller(user) ? (
              <MyAuctionGridWidget
                onEdit={onEdit}
                onDelete={onDelete}
                listingType="current"
                onView={onView}
              />
            ) : (
              <EnrolledAuctionGridWidget refresh={refreshAuctionList} />
            ))}
          {selectedTab === 1 &&
            refreshAuctionList &&
            (userIsSeller(user) ? (
              <MyAuctionGridWidget
                onEdit={onEdit}
                onDelete={onDelete}
                listingType="future"
                onView={onView}
              />
            ) : (
              <NonEnrolledAuctionGridWidget refresh={refreshAuctionList} />
            ))}
          {selectedTab === 2 && refreshAuctionList && (
            <MyAuctionGridWidget
              onEdit={onEdit}
              onDelete={onDelete}
              listingType="past"
              onView={onView}
            />
          )}
        </Box>
      </Box>
      {showAuctionFormModal && (
        <FormModal
          open={showAuctionFormModal}
          onClose={() => setShowAuctionFormModal(false)}
          size="sm"
        >
          <AuctionForm
            onSuccess={onAuctionCreate}
            auction_id={auctionDetail?.id}
            context="seller"
          />
        </FormModal>
      )}
      {showDeletingModal && (
        <ProgressModal open={showDeletingModal}>
          <Typography variant="h4" sx={{ fontStyle: 'italic' }}>
            Please wait... Deleting is in progress
          </Typography>
        </ProgressModal>
      )}
    </>
  );
};

export default MyAccountAuctionsPage;
